/**
 * WlAddons — gestão de add-ons (cadastro) no painel White Label.
 *
 * Espelha funcionalmente /admin/addons (Lovable root) — sem aba de sincronização
 * Stripe nem diagnóstico. Operações vão por RPCs SECURITY DEFINER:
 *   wl_list_addons, wl_upsert_addon, wl_toggle_addon_active, wl_list_addon_providers.
 *
 * Sem ação de excluir — somente ativar/desativar.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Search,
  Plus,
  Pencil,
  Package,
  Check,
  X,
  ToggleRight,
  ToggleLeft,
  Users,
  Inbox,
  MessageSquare,
  Megaphone,
  Layers,
  LayoutGrid,
  ListChecks,
  HardDrive,
  Gift,
  Coins,
  Wallet,
  FileSignature,
  Link as LinkIcon,
  Plug,
  Instagram,
} from "lucide-react";
import { useWlPermissions } from "@/contexts/WlPermissionsContext";
import {
 useWlAddons,
 useWlAddonProviders,
 useWlInstanceEligibleProviders,
 useWlAddonMutations,
  type WlAddon,
  type WlProviderPrice,
} from "@/hooks/useWlAddons";
import { useWlPlans } from "@/hooks/useWlPlans";
import { showToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { StatusDot } from "@/components/tenant/SettingsInfoRow";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
} from "@/components/layout/list-page";
import { applyCurrencyMask, parseCurrency } from "@/lib/input-masks";
import { RecurrenceOptionsEditor } from "@/components/wl/RecurrenceOptionsEditor";
import {
  RecurrenceOptionsInlineEditor,
  type PendingRecurrence,
} from "@/components/wl/RecurrenceOptionsInlineEditor";
import { useWlAddonRecurrenceMutations } from "@/hooks/wl/useWlRecurrenceOptions";
import { useWlAddonOneTimeOptionMutations } from "@/hooks/wl/useWlAddonOneTimeOption";
import {
  OneTimeOptionEditor,
  OneTimeOptionInlineEditor,
  type PendingOneTimeOption,
} from "@/components/wl/OneTimeOptionEditor";

import {
  WlBillingIntegrationSection,
  type WlBillingIntegrationValue,
} from "@/components/wl/WlBillingIntegrationSection";

// Catálogo de tipos suportados — mesmo do /admin/addons
const ADDON_TYPES = [
  { value: "agent", label: "Agentes Adicionais", icon: Users },
  { value: "inbox", label: "Inboxes Adicionais", icon: Inbox },
  // 'instance' separa o motor (provider_instance) da inbox lógica. Add-on
  // incrementa effective_max_instances via get_tenant_effective_limits (já
  // suporta esse addon_type desde v1.10.0). Tem provider_prices igual ao inbox.
  { value: "instance", label: "Instâncias Adicionais", icon: Plug },
  // v1.9.0: add-on que libera instância do canal Instagram (regra de negócio
  // espelhada do tipo 'instance', mas o provider precisa ter channel_type='instagram').
  { value: "instagram_instance", label: "Instagram Adicionais", icon: Instagram },
  { value: "channel", label: "Canais Adicionais", icon: MessageSquare },
  { value: "module", label: "Módulo Campanha", icon: Megaphone },
  { value: "crm_module", label: "Módulo CRM", icon: LayoutGrid },
  { value: "financial_module", label: "Módulo Financeiro", icon: Wallet },
  { value: "tasks_module", label: "Módulo Tarefas", icon: ListChecks },
  { value: "task_pack", label: "Pacote de Tarefas", icon: ListChecks },
  { value: "task_storage", label: "Armazenamento de Tarefas", icon: HardDrive },
  { value: "referral_tier", label: "Plano de Indicação", icon: Gift },
  { value: "referral_credits_pack", label: "Pacote de Créditos de Indicação", icon: Coins },
  { value: "digital_contracts_module", label: "Módulo Contrato Digital", icon: FileSignature },
] as const;

type AddonForm = Partial<WlAddon> & {
  slug: string;
  name: string;
  addon_type: string;
};

const EMPTY: AddonForm = {
  slug: "",
  name: "",
  description: "",
  addon_type: "agent",
  task_quota: null,
  storage_quota_mb: null,
  max_referral_campaigns: null,
  max_referral_programs: null,
  max_referral_affiliates: null,
  referral_bonus_credits: 0,
  conversion_credit_quota: 0,
  price_monthly: 0,
  price_yearly: 0,
  setup_fee: 0,
  has_setup_fee: false,
  unit_value: 1,
  max_quantity: null,
  min_plan_required: null,
  available_plans: [],
  sort_order: 0,
  // IDs do Stripe (editáveis no painel WL).
  stripe_product_id: null,
  stripe_price_monthly_id: null,
  stripe_price_yearly_id: null,
  stripe_setup_price_id: null,
  // Multi-gateway v1 (Fase 3).
  billing_integration: "legado",
  billing_gateway_slug: null,
  uaizy_master_service_id: null,
  // Modo de cobrança — default 'recorrente' (mantém comportamento legado).
  billing_mode: "recorrente",
  // v1.5.0: provider liberado para add-on de instância V1.
  provider_id: null,
  is_active: true,
};

const fmtPrice = (v: number | null) =>
  v && v > 0 ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v) : "—";

const getIcon = (type: string) => ADDON_TYPES.find((t) => t.value === type)?.icon || Package;

export default function WlAddons() {
  const { canCreate: canCreatePerm, canEdit: canEditPerm } = useWlPermissions();
  const canCreate = canCreatePerm("wl.addons");
  const canEdit = canEditPerm("wl.addons");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("__all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  const { data: addons = [], isLoading } = useWlAddons({
    includeInactive: statusFilter !== "active",
    search: search || null,
    type: typeFilter === "__all" ? null : typeFilter,
  });
  const filtered = useMemo(
    () => (statusFilter === "inactive" ? addons.filter((a) => !a.is_active) : addons),
    [addons, statusFilter],
  );
  const { data: providers = [] } = useWlAddonProviders();
  // v1.5.0: providers elegíveis (whatsapp / whatsapp_oficial) p/ add-on de instância V1.
  const { data: instanceEligibleProviders = [] } = useWlInstanceEligibleProviders();
  const { data: plans = [] } = useWlPlans({ includeInactive: false });
  const { upsert, toggleActive } = useWlAddonMutations();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WlAddon | null>(null);
  const [form, setForm] = useState<AddonForm>(EMPTY);
  const [providerPrices, setProviderPrices] = useState<WlProviderPrice[]>([]);
  // Periodicidades em memória — usadas apenas no fluxo de CRIAÇÃO de add-on V.1,
  // já que o id do add-on ainda não existe para persistir por linha.
  const [pendingRecurrences, setPendingRecurrences] = useState<PendingRecurrence[]>([]);
  // Opção avulsa pendente — usada apenas no fluxo de CRIAÇÃO de add-on V.1
  // quando billing_mode='avulso'.
  const [pendingOneTime, setPendingOneTime] = useState<PendingOneTimeOption | null>(null);
  // Mutations de periodicidade de add-on (usadas no submit pós-criação V.1).
  const addonRecurrenceM = useWlAddonRecurrenceMutations(null);
  const addonOneTimeM = useWlAddonOneTimeOptionMutations(null);

  // Mantém a lista de preços por provider sincronizada com os providers carregados.
  useEffect(() => {
    if (!dialogOpen) return;
    setProviderPrices((prev) =>
      providers.map((p) => {
        const existing = prev.find((x) => x.provider_id === p.id);
        return (
          existing || {
            provider_id: p.id,
            price_monthly: 0,
            setup_fee: 0,
            is_active: false,
          }

        );
      }),
    );
  }, [providers, dialogOpen]);

  const totals = useMemo(
    () => ({
      total: addons.length,
      active: addons.filter((a) => a.is_active).length,
      inactive: addons.filter((a) => !a.is_active).length,
    }),
    [addons],
  );

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setPendingRecurrences([]);
    setPendingOneTime(null);
    setProviderPrices(
      providers.map((p) => ({
        provider_id: p.id,
        price_monthly: 0,
        setup_fee: 0,
        is_active: false,
      })),
    );


    setDialogOpen(true);
  };

  const openEdit = (a: WlAddon) => {
    setEditing(a);
    setForm({ ...EMPTY, ...a });
    setPendingRecurrences([]);
    setPendingOneTime(null);
    setProviderPrices(
      providers.map((p) => {
        const existing = a.provider_prices.find((pp) => pp.provider_id === p.id);
        return (

          existing || {
            provider_id: p.id,
            price_monthly: 0,
            setup_fee: 0,
            is_active: false,
          }
        );
      }),

    );
    setDialogOpen(true);
  };

  const updatePp = (idx: number, field: keyof WlProviderPrice, value: any) =>
    setProviderPrices((prev) => prev.map((pp, i) => (i === idx ? { ...pp, [field]: value } : pp)));

  const togglePlanAvail = (planId: string) =>
    setForm((f) => ({
      ...f,
      available_plans: f.available_plans?.includes(planId)
        ? f.available_plans.filter((p) => p !== planId)
        : [...(f.available_plans || []), planId],
    }));

  // Multi-gateway v1 — derivações para validação e UI.
  const isV1 = form.billing_integration === "integracao_v1";
  const isUaizy = isV1 && form.billing_gateway_slug === "uaizy";
  const priceFromMaster = isUaizy;

  // Modo de cobrança (recorrente vs. avulso).
  const billingMode = (form.billing_mode || "recorrente") as
    | "recorrente"
    | "avulso";
  const isRecurring = billingMode === "recorrente";
  const isOneTime = billingMode === "avulso";
  // Periodicidades ativas (apenas relevante na CRIAÇÃO V.1 recorrente).
  const activePending = pendingRecurrences.filter((r) => r.is_active);
  const isCreatingV1 = !editing && isV1;

  // Aviso de incompatibilidade: planos selecionados em "available_plans" que
  // estão em modo diferente do add-on não conseguirão combinar com ele.
  const incompatiblePlans = useMemo(() => {
    const selectedIds = form.available_plans || [];
    return plans
      .filter((p) => selectedIds.includes(p.id))
      .filter(
        (p) =>
          (p.billing_integration ?? "legado") !== form.billing_integration ||
          (p.billing_gateway_slug ?? null) !== (form.billing_gateway_slug ?? null),
      );
  }, [plans, form.available_plans, form.billing_integration, form.billing_gateway_slug]);

  const submit = async () => {
    if (!form.slug || !form.name) {
      showToast.error("Preencha slug e nome");
      return;
    }
    if (isV1 && !form.billing_gateway_slug) {
      showToast.error("Selecione o gateway de cobrança");
      return;
    }
    // v1.5.0: add-on de instância V1 exige um provider liberado.
    // v1.9.0: idem para instagram_instance.
    if (isV1 && (form.addon_type === "instance" || form.addon_type === "instagram_instance") && !form.provider_id) {
      showToast.error("Selecione o provider liberado para o add-on de instância");
      return;
    }
    // Validação pré-submit (somente criação V.1).
    if (isCreatingV1 && isRecurring) {
      if (activePending.length === 0) {
        showToast.error("Configure pelo menos uma periodicidade ativa");
        return;
      }
      const invalid = activePending.find(
        (r) => !r.uaizy_master_service_id || !(r.price > 0),
      );
      if (invalid) {
        showToast.error(
          `Periodicidade ${invalid.recurrence_interval}: selecione o serviço e informe o preço`,
        );
        return;
      }
    }
    if (isCreatingV1 && isOneTime) {
      if (
        !pendingOneTime ||
        !pendingOneTime.uaizy_master_service_id ||
        !(pendingOneTime.price > 0)
      ) {
        showToast.error(
          "Configure a opção avulsa: selecione o serviço e informe o preço",
        );
        return;
      }
    }
    // OBS: a vinculação do serviço master agora é por periodicidade
    // (plan_addon_recurrence_options), não mais no nível do add-on.
    try {
      const addonId = await upsert.mutateAsync({
        id: editing?.id ?? null,
        payload: {
          ...form,
          // Vínculo do serviço master agora é por periodicidade
          // (plan_addon_recurrence_options). No nível do add-on, sempre enviar
          // null quando for V.1 — evita disparar a validação legada do trigger.
          uaizy_master_service_id: isV1 ? null : form.uaizy_master_service_id,
        },
        provider_prices: (form.addon_type === "inbox" || form.addon_type === "instance" || form.addon_type === "instagram_instance") ? providerPrices : [],
      });

      // Pós-criação V.1: persistir cada periodicidade ativa sequencialmente
      // via RPC já existente. Erros aqui propagam para o catch externo, mas o
      // add-on permanece criado — o usuário pode reabrir em modo edição.
      if (isCreatingV1 && addonId) {
        if (isRecurring) {
          for (const r of activePending) {
            await addonRecurrenceM.upsert.mutateAsync({
              id: null,
              payload: {
                addon_id: addonId,
                recurrence_interval: r.recurrence_interval,
                uaizy_master_service_id: r.uaizy_master_service_id,
                price: r.price,
                setup_fee: r.setup_fee,
                discount_percent: r.discount_percent,
                is_active: true,
              },
            });
          }
        } else if (isOneTime && pendingOneTime) {
          await addonOneTimeM.upsert.mutateAsync({
            id: null,
            payload: {
              addon_id: addonId,
              uaizy_master_service_id: pendingOneTime.uaizy_master_service_id,
              price: pendingOneTime.price,
              setup_fee: pendingOneTime.setup_fee,
              discount_percent: pendingOneTime.discount_percent,
              is_active: pendingOneTime.is_active,
            },
          });
        }
      }

      showToast.success(editing ? "Add-on atualizado" : "Add-on criado");
      setDialogOpen(false);
    } catch (e: any) {
      showToast.error(e?.message || "Falha ao salvar add-on");
    }
  };


  const handleToggle = async (a: WlAddon) => {
    try {
      await toggleActive.mutateAsync({ id: a.id, active: !a.is_active });
      showToast.success(`Add-on ${!a.is_active ? "ativado" : "desativado"}`);
    } catch (e: any) {
      showToast.error(e?.message || "Falha ao alterar status");
    }
  };

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Add-ons"
        description="Cadastro de add-ons disponíveis para contratação"
        actions={
          canCreate ? (
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1" /> Novo Add-on
            </Button>
          ) : null
        }
      />

      <ListPageKpis columns={3}>
        <KpiCard color="purple" icon={Package} label="Total" value={isLoading ? "…" : totals.total} />
        <KpiCard
          color="green"
          icon={Check}
          label="Ativos"
          value={isLoading ? "…" : totals.active}
          onClick={() => setStatusFilter(statusFilter === "active" ? "all" : "active")}
          active={statusFilter === "active"}
        />
        <KpiCard
          color="red"
          icon={X}
          label="Inativos"
          value={isLoading ? "…" : totals.inactive}
          onClick={() => setStatusFilter(statusFilter === "inactive" ? "all" : "inactive")}
          active={statusFilter === "inactive"}
        />
      </ListPageKpis>

      <ListPageFiltersBar isFetching={isLoading}>
        <div className="relative flex-1 min-w-[220px] sm:flex-none sm:w-[280px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
          <Input
            className={cn(LIST_FILTERS_INPUT_CLASS, "pl-7 sm:pl-8")}
            placeholder="Buscar por slug ou nome…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput)}
            onBlur={() => searchInput !== search && setSearch(searchInput)}
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, "sm:w-[220px]")}>
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todos tipos</SelectItem>
            {ADDON_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      <ListPageContent fullBleed>
        {isLoading ? (
          <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
        ) : filtered.length === 0 ? (
          <p className="p-12 text-center text-sm text-muted-foreground">Nenhum add-on encontrado</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs font-medium">Add-on</TableHead>
                <TableHead className="text-xs font-medium">Tipo</TableHead>
                <TableHead className="text-xs font-medium text-center">Modo</TableHead>
                <TableHead className="text-xs font-medium text-right">Preço</TableHead>
                <TableHead className="text-xs font-medium text-center">Qtd</TableHead>
                <TableHead className="text-xs font-medium text-center">Providers</TableHead>
                <TableHead className="text-xs font-medium text-center">Status</TableHead>
                <TableHead className="text-xs font-medium text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((a) => {
                const Icon = getIcon(a.addon_type);
                const typeLbl = ADDON_TYPES.find((t) => t.value === a.addon_type)?.label || a.addon_type;
                return (
                  <TableRow key={a.id}>
                    <TableCell className="py-2 text-xs">
                      <div className="font-medium">{a.name}</div>
                      <div className="text-[11px] text-muted-foreground">{a.slug}</div>
                    </TableCell>
                    <TableCell className="py-2 text-xs">
                      <span className="inline-flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        {typeLbl}
                      </span>
                    </TableCell>
                    <TableCell className="py-2 text-xs text-center">
                      {a.billing_integration === "integracao_v1" ? (
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-primary/40 text-primary">
                          V.1{a.billing_gateway_slug ? ` · ${a.billing_gateway_slug}` : ""}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                          Legado
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="py-2 text-xs text-right">{fmtPrice(a.price_monthly)}</TableCell>
                    <TableCell className="py-2 text-xs text-center">{a.unit_value}</TableCell>
                    <TableCell className="py-2 text-xs text-center">
                      {(a.addon_type === "inbox" || a.addon_type === "instance" || a.addon_type === "instagram_instance") ? (a.provider_prices.filter((p) => p.is_active).length || "—") : "—"}
                    </TableCell>
                    <TableCell className="py-2">
                      <span className="inline-flex items-center gap-1.5 text-xs text-foreground whitespace-nowrap">
                        <span className={cn("h-2 w-2 rounded-full shrink-0", a.is_active ? "bg-green-500" : "bg-zinc-400")} />
                        {a.is_active ? "Ativo" : "Inativo"}
                      </span>
                    </TableCell>
                    <TableCell className="py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" disabled={!canEdit} onClick={() => openEdit(a)} title="Editar">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!canEdit || toggleActive.isPending}
                          onClick={() => handleToggle(a)}
                          title={a.is_active ? "Desativar" : "Ativar"}
                        >
                          {a.is_active ? (
                            <ToggleRight className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </ListPageContent>

      {/* ---- Dialog Criar/Editar ---- */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <DialogTitle>{editing ? "Editar Add-on" : "Novo Add-on"}</DialogTitle>
                <DialogDescription>Configure um add-on para contratação</DialogDescription>
              </div>
              <button
                type="button"
                onClick={() => setForm({ ...form, is_active: !form.is_active })}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors shrink-0",
                  form.is_active
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20"
                    : "border-muted-foreground/30 bg-muted text-muted-foreground hover:bg-muted",
                )}
              >
                <span
                  className={cn(
                    "h-2 w-2 rounded-full",
                    form.is_active ? "bg-emerald-500" : "bg-zinc-400",
                  )}
                />
                {form.is_active ? "Ativo" : "Inativo"}
              </button>
            </div>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Slug *</Label>
                <Input value={form.slug} disabled={!!editing} maxLength={60}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input value={form.name} maxLength={80}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea value={form.description || ""} rows={2} maxLength={500}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select value={form.addon_type} onValueChange={(v) => setForm({ ...form, addon_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ADDON_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Qtd por Compra</Label>
                <Input type="number" value={form.unit_value || 1}
                  onChange={(e) => setForm({ ...form, unit_value: parseInt(e.target.value) || 1 })} />
              </div>
            </div>

            {/* v1.5.0 — Provider liberado: aparece apenas para add-on do tipo
                "Instâncias adicionais" no modo integracao_v1. Ao contratar este
                add-on, libera-se cota de instâncias do provider selecionado para
                criação manual em /tenant/inboxes (modal "Adicionar Instância"). */}
            {(form.addon_type === "instance" || form.addon_type === "instagram_instance") && isV1 && (
              <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-1.5">
                  {form.addon_type === "instagram_instance" ? (
                    <Instagram className="h-3.5 w-3.5" />
                  ) : (
                    <Plug className="h-3.5 w-3.5" />
                  )}{" "}
                  Provider liberado *
                </Label>
                <p className="text-[11px] text-muted-foreground">
                  {form.addon_type === "instagram_instance"
                    ? "Selecione o provider (canal Instagram) que o tenant poderá usar ao criar instâncias adicionais deste add-on."
                    : "Selecione o provider (canal WhatsApp ou WhatsApp API Oficial) que o tenant poderá usar ao criar instâncias adicionais deste add-on."}
                </p>
                <Select
                  value={form.provider_id || ""}
                  onValueChange={(v) => setForm({ ...form, provider_id: v || null })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um provider…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      // v1.9.0: filtra providers conforme o tipo de add-on selecionado.
                      // 'instance' → whatsapp + whatsapp_oficial.
                      // 'instagram_instance' → instagram.
                      const allowedChannels = form.addon_type === "instagram_instance"
                        ? ["instagram"]
                        : ["whatsapp", "whatsapp_oficial"];
                      const filtered = instanceEligibleProviders.filter((p) =>
                        allowedChannels.includes(p.channel_type),
                      );
                      if (filtered.length === 0) {
                        return (
                          <div className="px-3 py-2 text-xs text-muted-foreground italic">
                            Nenhum provider ativo elegível
                          </div>
                        );
                      }
                      return filtered.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.display_name || p.name}
                          <span className="ml-2 text-[10px] text-muted-foreground">
                            ({p.channel_type === "whatsapp_oficial"
                              ? "WhatsApp API Oficial"
                              : p.channel_type === "instagram"
                              ? "Instagram"
                              : "WhatsApp"})
                          </span>
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
            )}


            {/* Cotas — Tarefas */}
            {(form.addon_type === "task_pack" || form.addon_type === "task_storage") && (
              <>
                <Separator />
                <div className="grid grid-cols-2 gap-3">
                  {form.addon_type === "task_pack" && (
                    <div className="space-y-1">
                      <Label className="text-xs">Tarefas/Mês incluídas</Label>
                      <Input type="number" value={form.task_quota ?? ""}
                        onChange={(e) => setForm({ ...form, task_quota: e.target.value ? parseInt(e.target.value) : null })} />
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {form.addon_type === "task_pack" ? "Storage incluso (MB)" : "Storage por unidade (MB)"}
                    </Label>
                    <Input type="number" value={form.storage_quota_mb ?? ""}
                      onChange={(e) => setForm({ ...form, storage_quota_mb: e.target.value ? parseInt(e.target.value) : null })} />
                  </div>
                </div>
              </>
            )}

            {/* Cotas — Referral */}
            {form.addon_type === "referral_tier" && (
              <>
                <Separator />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Máx. Campanhas</Label>
                    <Input type="number" value={form.max_referral_campaigns ?? ""}
                      placeholder="vazio = ilimitado"
                      onChange={(e) => setForm({ ...form, max_referral_campaigns: e.target.value ? parseInt(e.target.value) : null })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Máx. Programas</Label>
                    <Input type="number" value={form.max_referral_programs ?? ""}
                      placeholder="vazio = ilimitado"
                      onChange={(e) => setForm({ ...form, max_referral_programs: e.target.value ? parseInt(e.target.value) : null })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Máx. Afiliados</Label>
                    <Input type="number" value={form.max_referral_affiliates ?? ""}
                      placeholder="vazio = ilimitado"
                      onChange={(e) => setForm({ ...form, max_referral_affiliates: e.target.value ? parseInt(e.target.value) : null })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Créditos Cortesia</Label>
                    <Input type="number" value={form.referral_bonus_credits ?? 0}
                      onChange={(e) => setForm({ ...form, referral_bonus_credits: parseInt(e.target.value) || 0 })} />
                  </div>
                </div>
              </>
            )}

            {form.addon_type === "referral_credits_pack" && (
              <>
                <Separator />
                <div className="space-y-1">
                  <Label className="text-xs">Créditos por Compra *</Label>
                  <Input type="number" value={form.conversion_credit_quota ?? 0}
                    onChange={(e) => setForm({ ...form, conversion_credit_quota: parseInt(e.target.value) || 0 })} />
                </div>
              </>
            )}

            {/* Bloco Integração (multi-gateway v1). Recebe aviso de incompatibilidade
                quando algum plano selecionado em "available_plans" está em modo
                diferente do add-on. */}
            <WlBillingIntegrationSection
              value={{
                billing_integration: form.billing_integration,
                billing_gateway_slug: form.billing_gateway_slug,
                uaizy_master_service_id: form.uaizy_master_service_id,
              }}
              onChange={(next: WlBillingIntegrationValue) => setForm({ ...form, ...next })}
              helperSlot={
                incompatiblePlans.length > 0 ? (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-300">
                    Atenção: {incompatiblePlans.length} plano(s) selecionado(s) em
                    "Planos Disponíveis" estão em modo diferente
                    {" "}({incompatiblePlans.map((p) => p.name).join(", ")}) e não
                    poderão combinar com este add-on. Ajuste o modo ou remova-os.
                  </div>
                ) : undefined
              }
            />

            {/* Tipo de cobrança do add-on — só faz sentido em add-ons V.1.
                'Recorrente' usa periodicidades (plan_addon_recurrence_options);
                'Avulso' usa uma única opção (plan_addon_one_time_options). */}
            {isV1 && (
              <div className="rounded-md border p-3 space-y-2">
                <Label className="text-sm font-semibold">Tipo de cobrança</Label>
                <p className="text-[11px] text-muted-foreground">
                  Define se o add-on é vendido por periodicidade (recorrente) ou
                  como compra única (avulso). Não é possível alternar quando já
                  existem opções configuradas no outro modo.
                </p>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  {(["recorrente", "avulso"] as const).map((mode) => {
                    const active = billingMode === mode;
                    return (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setForm({ ...form, billing_mode: mode })}
                        className={cn(
                          "rounded-md border px-3 py-2 text-xs font-medium transition-colors",
                          active
                            ? "border-primary/40 bg-primary/10 text-primary"
                            : "border-muted-foreground/30 bg-muted/30 text-muted-foreground hover:bg-muted/50",
                        )}
                      >
                        {mode === "recorrente" ? "Recorrente" : "Avulso (compra única)"}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Preço Base Mensal / Taxa de Setup Base — usados apenas pelo fluxo LEGADO (Stripe).
                Em integracao_v1 os valores vêm das opções de periodicidade / serviço avulso do tenant master. */}
            {!isV1 && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Preço Base Mensal *</Label>
                  <Input
                    value={form.price_monthly ? applyCurrencyMask(form.price_monthly) : ""}
                    onChange={(e) => setForm({ ...form, price_monthly: parseCurrency(e.target.value) })}
                    disabled={priceFromMaster}
                  />
                  {priceFromMaster && (
                    <p className="text-[11px] text-muted-foreground">
                      Preço vem do serviço do tenant master.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Taxa de Setup Base</Label>
                  <Input
                    value={form.setup_fee ? applyCurrencyMask(form.setup_fee) : ""}
                    onChange={(e) => setForm({ ...form, setup_fee: parseCurrency(e.target.value) })}
                    disabled={priceFromMaster}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Planos Disponíveis</Label>
                <span className="text-[11px] text-muted-foreground">
                  {form.available_plans?.length || 0} de {plans.length} selecionados
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {plans.map((plan) => {
                  const selected = form.available_plans?.includes(plan.id);
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => togglePlanAvail(plan.id)}
                      className={cn(
                        "w-full min-h-[42px] rounded-md border px-3 py-2 text-xs transition-all",
                        "flex items-center justify-center gap-1.5 text-center font-medium",
                        selected
                          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20"
                          : "border-dashed border-muted-foreground/30 bg-muted/30 text-muted-foreground hover:bg-muted/50",
                      )}
                    >
                      {selected ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                      {plan.name}
                    </button>
                  );
                })}
                {plans.length === 0 && (
                  <p className="col-span-full text-xs text-muted-foreground italic">
                    Nenhum plano cadastrado
                  </p>
                )}
              </div>
            </div>




            {/* Integração Stripe (base do add-on) — IDs do produto/preços para o integrador financeiro.
                Oculto quando billing_integration = integracao_v1 (gateway uaizy), pois não usa Stripe. */}
            {!isV1 && (
              <div className="rounded-md border p-3 space-y-3">
                <Label className="text-sm font-semibold">Integração Stripe</Label>
                <p className="text-[11px] text-muted-foreground">
                  IDs do Stripe para processamento de pagamentos deste add-on.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Product ID</Label>
                    <Input
                      placeholder="prod_..."
                      value={form.stripe_product_id || ""}
                      onChange={(e) => setForm({ ...form, stripe_product_id: e.target.value || null })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Price Mensal ID</Label>
                    <Input
                      placeholder="price_..."
                      value={form.stripe_price_monthly_id || ""}
                      onChange={(e) => setForm({ ...form, stripe_price_monthly_id: e.target.value || null })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Price Anual ID</Label>
                    <Input
                      placeholder="price_..."
                      value={form.stripe_price_yearly_id || ""}
                      onChange={(e) => setForm({ ...form, stripe_price_yearly_id: e.target.value || null })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Setup Price ID</Label>
                    <Input
                      placeholder="price_..."
                      value={form.stripe_setup_price_id || ""}
                      onChange={(e) => setForm({ ...form, stripe_setup_price_id: e.target.value || null })}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Preços por provider — tipos que possuem motor de provider (inbox e instance).
                Oculto em integracao_v1 (gateway uaizy), pois é estrutura do fluxo legado Stripe. */}
            {(form.addon_type === "inbox" || form.addon_type === "instance" || form.addon_type === "instagram_instance") && providers.length > 0 && !isV1 && (
              <>
                <Separator />
                {/* Header da seção: ícone + título + contagem de providers ativos */}
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold flex items-center gap-1.5">
                    <Layers className="h-3.5 w-3.5" /> Preços por Provider
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    {providerPrices.filter((p) => p.is_active).length}/{providerPrices.length} ativos
                  </span>
                </div>
                <div className="space-y-2">
                  {providerPrices.map((pp, idx) => {
                    const provider = providers.find((p) => p.id === pp.provider_id);
                    return (
                      <div
                        key={pp.provider_id}
                        className={cn(
                          "rounded-lg border p-3 transition-colors",
                          pp.is_active
                            ? "border-primary/30 bg-primary/[0.03] ring-1 ring-primary/10"
                            : "border-border bg-card",
                        )}
                      >
                        {/* Header do card: badge status clicável + nome do provider */}
                        <div className="flex items-center gap-2.5 mb-3">
                          <Badge
                            variant="outline"
                            onClick={() => updatePp(idx, "is_active", !pp.is_active)}
                            className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider cursor-pointer select-none"
                          >
                            <StatusDot tone={pp.is_active ? "emerald" : "muted"} />
                            {pp.is_active ? "Ativo" : "Inativo"}
                          </Badge>
                          {/* Exibe display_name/name — slug é sigiloso do root admin. */}
                          <span className="text-sm font-medium text-foreground">
                            {provider?.display_name || provider?.name || pp.provider_id}
                          </span>
                        </div>

                        {/* Grid de preços — preço mensal + setup */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-[11px] text-muted-foreground">Preço mensal</Label>
                            <Input
                              className="h-9"
                              placeholder="R$ 0,00"
                              value={pp.price_monthly ? applyCurrencyMask(pp.price_monthly) : ""}
                              disabled={!pp.is_active}
                              onChange={(e) => updatePp(idx, "price_monthly", parseCurrency(e.target.value))}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[11px] text-muted-foreground">Setup</Label>
                            <Input
                              className="h-9"
                              placeholder="R$ 0,00"
                              value={pp.setup_fee ? applyCurrencyMask(pp.setup_fee) : ""}
                              disabled={!pp.is_active}
                              onChange={(e) => updatePp(idx, "setup_fee", parseCurrency(e.target.value))}
                            />
                          </div>

                          {/* Inputs Stripe por provider (somente quando o provider está ativo). */}
                          {pp.is_active && (
                            <>
                              <div className="space-y-1">
                                <Label className="text-[11px] text-muted-foreground">Stripe Price Mensal</Label>
                                <Input
                                  className="h-9"
                                  placeholder="price_..."
                                  value={pp.stripe_price_monthly_id || ""}
                                  onChange={(e) => updatePp(idx, "stripe_price_monthly_id", e.target.value || null)}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-[11px] text-muted-foreground">Stripe Price Setup</Label>
                                <Input
                                  className="h-9"
                                  placeholder="price_..."
                                  value={pp.stripe_setup_price_id || ""}
                                  onChange={(e) => updatePp(idx, "stripe_setup_price_id", e.target.value || null)}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Editor de cobrança — varia conforme billing_mode.
                Recorrente: editor de periodicidades (RecurrenceOptions*).
                Avulso: editor de opção única (OneTimeOption*). */}
            {isV1 && isRecurring && editing && (
              <RecurrenceOptionsEditor entity="addon" entityId={editing.id} />
            )}
            {isV1 && isRecurring && !editing && (
              <RecurrenceOptionsInlineEditor
                value={pendingRecurrences}
                onChange={setPendingRecurrences}
              />
            )}
            {isV1 && isOneTime && editing && (
              <OneTimeOptionEditor entityId={editing.id} />
            )}
            {isV1 && isOneTime && !editing && (
              <OneTimeOptionInlineEditor
                value={pendingOneTime}
                onChange={setPendingOneTime}
              />
            )}
          </div>




          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={upsert.isPending}>
              {upsert.isPending ? "Salvando…" : editing ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ListPageLayout>
  );
}
